(() => {
  const boundRoots = new WeakSet();
  const controllers = new WeakMap();
  function uniqueIds(root, document) {
    const renamed = new Map();
    for (const element of [root, ...root.querySelectorAll('[id]')]) {
      const oldId = element.id;
      if (!oldId || document.getElementById(oldId) === element) continue;
      let suffix = 2;
      let newId = `${oldId}-${suffix}`;
      while (document.getElementById(newId)) newId = `${oldId}-${++suffix}`;
      element.id = newId;
      renamed.set(oldId, newId);
    }
    if (!renamed.size) return;
    for (const element of [root, ...root.querySelectorAll('*')]) {
      for (const attribute of ['for', 'aria-labelledby', 'aria-describedby']) {
        const value = element.getAttribute(attribute);
        if (value) element.setAttribute(attribute, value.split(/\s+/).map(id => renamed.get(id) || id).join(' '));
      }
      const action = element.getAttribute('action');
      if (action && action.includes('#')) {
        const hash = action.slice(action.indexOf('#') + 1);
        if (renamed.has(hash)) element.setAttribute('action', `${action.slice(0, action.indexOf('#'))}#${renamed.get(hash)}`);
      }
    }
  }

  function bind(root, environment) {
    if (boundRoots.has(root)) return null;
    const form = root.querySelector('.realm-newsletter-form');
    const button = form?.querySelector('[data-realm-newsletter-submit]');
    if (!form || !button) return null;
    boundRoots.add(root);
    root.dataset.newsletterBound = 'true';
    uniqueIds(root, environment.document);
    const label = button.querySelector?.('[data-realm-action-label]') || button;
    const initialLabel = label.textContent;
    const initiallyDisabled = button.disabled;
    const status = form.querySelector('[data-realm-newsletter-status]');
    const initialStatus = status ? { text: status.textContent, hidden: status.hidden } : null;
    let pending = false;
    let alive = true;
    let generation = 0;
    function reset() {
      generation++;
      pending = false;
      button.disabled = initiallyDisabled;
      label.textContent = initialLabel;
      button.removeAttribute('aria-busy');
      form.removeAttribute('aria-busy');
      if (status) {
        status.textContent = initialStatus.text;
        status.hidden = initialStatus.hidden;
      }
    }
    const onSubmit = event => {
      if (event.defaultPrevented) return;
      if (initiallyDisabled) { event.preventDefault(); return; }
      if (pending) { event.preventDefault(); return; }
      if (!form.checkValidity()) { event.preventDefault(); form.reportValidity(); return; }
      pending = true;
      const submission = ++generation;
      environment.defer(() => {
        if (!alive || submission !== generation) return;
        if (event.defaultPrevented) { reset(); return; }
        form.setAttribute('aria-busy', 'true');
        button.setAttribute('aria-busy', 'true');
        button.disabled = true;
        label.textContent = button.dataset.submittingLabel;
        if (status) { status.hidden = false; status.textContent = button.dataset.submittingLabel; }
      });
    };
    form.addEventListener('submit', onSubmit);
    environment.window.addEventListener('pageshow', reset);
    const response = form.querySelector('[data-realm-newsletter-response]');
    if (response && environment.document.activeElement === environment.document.body) response.focus();
    const controller = { reset, destroy() {
      alive = false;
      reset();
      environment.window.removeEventListener?.('pageshow', reset);
      form.removeEventListener?.('submit', onSubmit);
      boundRoots.delete(root);
      controllers.delete(root);
      delete root.dataset.newsletterBound;
    } };
    controllers.set(root, controller);
    return controller;
  }

  function mount(scope, environment) {
    const roots = [...scope.querySelectorAll('[data-realm-newsletter]')];
    if (scope.matches?.('[data-realm-newsletter]')) roots.unshift(scope);
    roots.forEach(root => bind(root, environment));
  }

  function unmount(scope) {
    const roots = [...scope.querySelectorAll('[data-realm-newsletter]')];
    if (scope.matches?.('[data-realm-newsletter]')) roots.unshift(scope);
    roots.forEach(root => controllers.get(root)?.destroy());
  }

  if (typeof module === 'object' && module.exports) module.exports = { bind, mount, unmount, uniqueIds };
  if (typeof window === 'undefined' || !window.document || window.RealmNewsletterMounted) return;
  window.RealmNewsletterMounted = true;
  const environment = { document: window.document, window, defer: callback => window.queueMicrotask(callback) };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => mount(document, environment), { once: true });
  else mount(document, environment);
  document.addEventListener('shopify:section:load', event => mount(event.target, environment));
  document.addEventListener('shopify:section:unload', event => unmount(event.target));
})();
